// The two ROSTER PAGE SHELLS — the frame every lens's dashboard renders inside, so that the three
// implementations of each page differ in their SECTIONS and nothing else.
//
// There are two of them because the roster has two page shapes, each served three ways:
//   • RosterScreenShell       — the cross-roster home (`/roster`): Label · Management · Booking.
//   • RosterArtistScreenShell — one artist under the lens (`/roster/artists/:id`): the same three.
//
// Both were hand-rolled six times before this. `.page` was BYTE-IDENTICAL in all six stylesheets, and the
// cross-roster header (`.head` / `.title` / `.sub`) byte-identical in all three — which is exactly the shape
// the component-library rule warns about, because copies do not carry a ruling evenly. They had already
// drifted in four ways, none of them decisions anyone recorded:
//   * the header's text column was `.headText` twice and `.headMain` once; its sub-line `.sub` twice and
//     `.subtitle` once;
//   * the loading skeleton was 200px, 200px and 420px;
//   * two lenses wrapped their loading/error states in the designed `.card` takeover block and the third
//     rendered a bare `EmptyState` on the page canvas;
//   * two artist dashboards had byte-identical skeletons in two different stylesheets.
//
// What the shell does NOT own is the query composition. Each lens knows its own reads — Management waits on
// two, Booking additionally requires `data.booking` — so the caller resolves those into a {@link
// RosterScreenState} and the shell owns only how each state is DRAWN. A shell that tried to own the queries
// would need a prop per lens and would still be wrong for the fourth one.
import type { ReactNode } from "react";
import { Button, EmptyState, Skeleton } from "@cntrl/ui";
import { ApiError } from "../../app/hooks/apiError.js";
import styles from "./RosterScreen.module.scss";

/**
 * Which of the four states a roster page is in, resolved by the caller from its own queries.
 *
 * A discriminated union rather than loose props, so the combinations that make no sense cannot be written:
 * an `error` with no way to retry, or an `empty` with no copy explaining what is absent.
 *
 * `empty` is the BORN-BLANK takeover — the whole page replaced by one designed state. Not every lens has
 * one, and that is a real product decision rather than an omission: the Booking pipeline deliberately keeps
 * its full page with each stage in its own empty state, because a named stage that is ABSENT is
 * indistinguishable from a stage nobody built. Such a lens simply never returns this status.
 */
export type RosterScreenState =
  | { status: "loading" }
  | { status: "error"; error?: unknown; onRetry: () => void }
  | { status: "empty"; title: string; description: string; action?: ReactNode }
  | { status: "ready" };

/** The error copy: the server's own message when it sent one, a plain retry prompt otherwise. */
function errorText(error: unknown): string {
  return error instanceof ApiError ? error.message : "Please try again.";
}

/**
 * The page-level state takeover: a paper block holding one designed state, centred, with a floor so it
 * cannot collapse to a sliver. Two of the three cross-roster screens already drew this and the third put a
 * bare `EmptyState` straight on the page canvas.
 */
function Takeover({ children }: { children: ReactNode }) {
  return <div className={styles.takeover}>{children}</div>;
}

/** Everything but `ready` — the shell renders its children in that case, so this never sees it. */
type RosterScreenAbsent = Exclude<RosterScreenState, { status: "ready" }>;

function StateBlock({ state, skeletonHeight }: { state: RosterScreenAbsent; skeletonHeight: number }) {
  if (state.status === "loading") {
    return (
      <Takeover>
        <div className={styles.skeletonWrap} role="status" aria-live="polite" aria-label="Loading">
          <Skeleton variant="block" width="100%" height={skeletonHeight} />
        </div>
      </Takeover>
    );
  }
  if (state.status === "error") {
    return (
      <Takeover>
        <EmptyState
          title="Couldn't load the roster"
          description={errorText(state.error)}
          action={<Button onClick={state.onRetry}>Retry</Button>}
        />
      </Takeover>
    );
  }
  return (
    <Takeover>
      <EmptyState title={state.title} description={state.description} action={state.action} />
    </Takeover>
  );
}

export interface RosterScreenShellProps {
  /** The page H1. It stands ALONE — no eyebrow, no crumb (PO 2026-08-17, `headings.guard.test.ts`). */
  title: ReactNode;
  /** The line under it — the caller composes the whole thing, since each lens counts different facts. */
  sub?: ReactNode;
  /** Header actions, right-aligned (Refresh, New task). */
  actions?: ReactNode;
  state: RosterScreenState;
  /** The loading block's height. A hint at the shape underneath, so the page does not jump on load. */
  skeletonHeight?: number;
  /**
   * The `ready` content.
   *
   * **Read your data defensively here.** JSX children are CONSTRUCTED at the call site whatever state this
   * shell goes on to draw, so a non-null assertion on a not-yet-loaded read throws on the first render —
   * the shell cannot protect an expression that has already been evaluated. Optional-chain it, or guard the
   * subtree with `{data?.thing && …}`. (The per-artist shell's callers early-return instead, which they can
   * because they have no header to keep on screen while loading.)
   */
  children?: ReactNode;
}

/**
 * The CROSS-ROSTER page shell: the frame, the header, and the four-state contract.
 *
 * The header renders in EVERY state, including loading — the org is known before its analytics are, and a
 * page whose title appears only after a fetch reads as a page that failed to load.
 */
export function RosterScreenShell({
  title, sub, actions, state, skeletonHeight = 200, children,
}: RosterScreenShellProps) {
  return (
    <section className={styles.page}>
      <header className={styles.head}>
        <div className={styles.headText}>
          <h1 className={styles.title}>{title}</h1>
          {sub && <p className={styles.sub}>{sub}</p>}
        </div>
        {actions && <div className={styles.headActions}>{actions}</div>}
      </header>

      {state.status === "ready" ? children : <StateBlock state={state} skeletonHeight={skeletonHeight} />}
    </section>
  );
}

export interface RosterArtistScreenShellProps {
  state: RosterScreenState;
  /**
   * The loading shape. Defaults to a header block over a four-card grid, which is what the Label and
   * Management dashboards actually render; a lens whose content is shaped differently (Booking leads with a
   * KPI strip over a table) passes its own, because a skeleton is a promise about what is coming.
   */
  skeleton?: ReactNode;
  children?: ReactNode;
}

/**
 * The PER-ARTIST page shell. No header slot, and no header — the ROSTER SHELL's context header is the one
 * header on these pages (PO, 2026-09-02). Two of the three used to stack their own under it, naming the
 * artist twice; `ArtistHeader` is deleted.
 *
 * A page's top-right ACTION therefore does not live here either: it registers into the shell's header
 * through `app/rosterHeaderAction.ts` for as long as the page is mounted. See there for why the shell
 * renders it but cannot derive it.
 */
export function RosterArtistScreenShell({ state, skeleton, children }: RosterArtistScreenShellProps) {
  if (state.status === "loading") {
    return (
      <section className={styles.page} role="status" aria-live="polite" aria-label="Loading">
        {skeleton ?? (
          <>
            <Skeleton variant="block" width="100%" height={120} />
            <div className={styles.skeletonGrid}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} variant="block" width="100%" height={140} />
              ))}
            </div>
          </>
        )}
      </section>
    );
  }
  if (state.status !== "ready") {
    return (
      <section className={styles.page}>
        {state.status === "error" ? (
          <EmptyState
            title="Couldn't load this artist"
            description={errorText(state.error)}
            action={<Button onClick={state.onRetry}>Retry</Button>}
          />
        ) : (
          <EmptyState title={state.title} description={state.description} action={state.action} />
        )}
      </section>
    );
  }
  return <section className={styles.page}>{children}</section>;
}
